import { BrButton, BrDivider, BrInput, BrLoading, BrSelect, Col, Row } from '@govbr-ds/react-components';
import styles from './baseDeCalculoFormItemNCM.module.scss';

import { useContext, useEffect, useState } from 'react';
import { useNotificacao } from '../../hooks/useNotificacao';
import {
  ICodigoNcm as ICodigoNcmBaseCalculo,
  IComponenteBaseDeCalculoPrimeiroAdquirenteouArrematacao,
} from '../../interfaces/IBaseDeCalculo';
import { ICodigoNcm } from '../../interfaces/ICodigoNcm';
import { formatCurrency, removeFormatCurrency } from '../../utils/formatCurrency';
import { NcmTable } from './components/baseDeCalculoLista/ncmTable';
import NCMViewContainerList from './components/ncmViewContainerList/ncmViewContainerList';
import { ITributoAdicionado, ITributos } from 'interfaces/ITributos';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from 'pages/PrimeiroAdquirente/lib/backEndRequests';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { validarDeducoes } from 'utils/validarDeducoes';
import { DiefCfemContext } from 'contexts/DiefCfemContext';

interface IComponentesBaseDeCalculoFormItemNCM {
  listaComponentesBaseCalculo?: IComponenteBaseDeCalculoPrimeiroAdquirenteouArrematacao[];
  setListaComponentesBaseCalculo?: React.Dispatch<
    React.SetStateAction<IComponenteBaseDeCalculoPrimeiroAdquirenteouArrematacao[]>
  >;
  listaCodigosNCM: ICodigoNcmBaseCalculo[];
  setListaCodigosNCM: React.Dispatch<React.SetStateAction<ICodigoNcmBaseCalculo[]>>;
  codigosNCM: ICodigoNcm[];
  type: 'Aquisição' | 'Arrematação' | 'Operação';
  isConsolidado?: boolean;
  isViewContainer?: boolean;
  isReadOnly?: boolean;
  numeroProcesso?: string;
}

function ComponentesBaseDeCalculoFormItemNCM({
  listaComponentesBaseCalculo,
  setListaComponentesBaseCalculo,
  listaCodigosNCM,
  setListaCodigosNCM,
  codigosNCM,
  type,
  isConsolidado = false,
  isViewContainer,
  isReadOnly,
  numeroProcesso,
}: Readonly<IComponentesBaseDeCalculoFormItemNCM>) {
  const [quantidade, setQuantidade] = useState('');
  const [valor, setValor] = useState('');
  const [codigoNCM, setCodigoNCM] = useState('');
  const [deducoesAdicionadas, setDeducoesAdicionadas] = useState<ITributoAdicionado[]>([]);
  const mostrarNotificacao = useNotificacao();

  const { getTributos } = useBackEndRequests();
  const context = useContext(DiefCfemContext);
  const mesExercicio = context?.declaracaoPrimeiroAdquirente.exercicioMes || -1;
  const anoExercicio = context?.declaracaoPrimeiroAdquirente.exercicioAno || -1;

  const {
    data: tributosPossiveisData,
    isLoading: tributosPossiveisLoading,
    error: tributosPossiveisError,
    refetch: refetchTributosPossiveis,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributos'],
    queryFn: () => getTributos(numeroProcesso?.replace(/[./]/g, '') || '', mesExercicio, anoExercicio),
    refetchOnWindowFocus: false,
  });

  const hasTributosData =
    !!tributosPossiveisData &&
    ((tributosPossiveisData.tributosEspeciais?.length ?? 0) > 0 ||
      (tributosPossiveisData.tributosRegulares?.length ?? 0) > 0);

  useEffect(() => {
    if (!!listaComponentesBaseCalculo && !!setListaComponentesBaseCalculo) {
      const novaLista = [...listaComponentesBaseCalculo];
      if (novaLista.length === 0) {
        setListaComponentesBaseCalculo(novaLista);
      }
    }
  }, []);

  const adicionarNCM = () => {
    const QUANTIDADE_MINIMA = 0.0001;
    const QUANTIDADE_MAXIMA = 1e11;

    if (!codigoNCM || !quantidade || !valor) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Preencha todos os campos referentes ao NCM.',
      });
      return;
    }

    if (parseFloat(quantidade) < QUANTIDADE_MINIMA) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Valor mínimo para quantidade é de ${QUANTIDADE_MINIMA}.`,
      });
      return;
    }

    if (parseFloat(quantidade) >= QUANTIDADE_MAXIMA) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Valor máximo para quantidade é de ${QUANTIDADE_MAXIMA}.`,
      });
      return;
    }

    if (listaCodigosNCM.find((item) => item.codigoNCM === codigoNCM)) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: `Já existe um código NCM ${codigoNCM} na lista.`,
      });
      return;
    }

    let errosValidacaoDeducoes = validarDeducoes(deducoesAdicionadas, tributosPossiveisData);
    if (errosValidacaoDeducoes !== '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: errosValidacaoDeducoes,
      });
      return;
    }

    if (removeFormatCurrency(valor) - deducoesAdicionadas.reduce((acc, item) => acc + item.valor, 0) < 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'O valor total das deduções não pode ser maior que o valor da aquisição.',
      });
      return;
    }

    setListaCodigosNCM([
      ...listaCodigosNCM,
      {
        codigoNCM: codigoNCM,
        quantidade: quantidade,
        valor: removeFormatCurrency(valor).toString(),
        tributosValores: deducoesAdicionadas,
      },
    ]);

    setCodigoNCM('');
    setQuantidade('');
    setValor('');
    setDeducoesAdicionadas([]);
  };

  const styleContainer = type === 'Arrematação' || isConsolidado ? styles.containerSemBorda : styles.container;

  return (
    <div className={styleContainer}>
      {isViewContainer && <NCMViewContainerList codigosNCM={listaCodigosNCM} />}
      <div className={styles.componentesTitle}>Informe o Código NCM</div>
      <div className={styles.form}>
        <form id='ncmForm' className={styles.form}>
          <Row className={styles.row}>
            <Col>
              <BrSelect
                label='Código NCM'
                className={styles.select}
                onChange={(value: string) => setCodigoNCM(value)}
                options={codigosNCM.map((item: ICodigoNcm) => ({
                  label: `${item?.codigo} - ${item.descricao}`,
                  value: item?.codigo,
                }))}
                value={codigoNCM}
                disabled={isReadOnly}
                placeholder='Selecione um código NCM...'
              />
            </Col>
            <Col>
              <BrInput
                label='Quantidade'
                autoComplete='off'
                required
                numeric={{
                  decimalScale: 2,
                  fixedDecimalScale: false,
                }}
                onChange={(e) => {
                  setQuantidade(e.target.value.replace(',', '.'));
                }}
                value={quantidade}
                disabled={isReadOnly}
              />
            </Col>
            <Col>
              <BrInput
                label={`Valor de ${type}`}
                autoComplete='off'
                numeric={{
                  prefix: 'R$ ',
                  thousandSeparator: '.',
                  decimalScale: 2,
                  fixedDecimalScale: false,
                }}
                required
                onChange={(e) => setValor(e.target.value)}
                value={valor}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
          <div className={styles.divider}>
            <BrDivider />
          </div>
          {type === 'Aquisição' && (
            <>
              {tributosPossiveisLoading ? (
                <BrLoading />
              ) : (
                hasTributosData && (
                  <>
                    <SecaoDeducoes
                      key={codigoNCM}
                      deducoesPossiveis={tributosPossiveisData}
                      deducoesAdicionadas={deducoesAdicionadas}
                      setDeducoesAdicionadas={setDeducoesAdicionadas}
                      valorReceitaBruta={valor}
                      semMargem
                    />
                    <div className={styles.divider}>
                      <BrDivider />
                    </div>
                  </>
                )
              )}
            </>
          )}
          {tributosPossiveisError && (
            <div className={styles.errorContainer}>
              <span className={styles.errorText}>Não foi possível atualizar a lista de tributos</span>
              <BrButton size='small' onClick={() => refetchTributosPossiveis()}>
                (Tentar novamente)
              </BrButton>
            </div>
          )}
          <Row>
            <Col className={styles.precoUnitario}>
              <label className={styles.label}>
                Preço Unitário
                <p className={styles.precoUnitarioText}>
                  {valor && quantidade
                    ? formatCurrency(
                        (
                          (removeFormatCurrency(valor) -
                            deducoesAdicionadas.reduce((acc, item) => acc + item.valor, 0)) /
                          parseFloat(quantidade)
                        ).toFixed(2)
                      )
                    : 'R$ 00,00'}
                </p>
              </label>
            </Col>
          </Row>

          <div className={styles.botao}>
            <BrButton
              onClick={adicionarNCM}
              secondary
              className={styles.botaoTerceario}
              size='small'
              disabled={isReadOnly}
            >
              <i className={`fas fa-plus fa-md  ${styles.columnIcon}`} />
              <span>Adicionar Código NCM</span>
            </BrButton>
          </div>
        </form>
      </div>
      {!isViewContainer && (
        <NcmTable
          listaCodigosNCM={listaCodigosNCM}
          isLoading={false}
          setListaCodigosNCM={setListaCodigosNCM}
          headers={[
            { field: 'codigoNCM', label: 'Código NCM' },
            { field: 'quantidade', label: 'Quantidade' },
            { field: 'precoUnitario', label: 'Preço Unitário' },
            { field: 'valor', label: `Valor de ${type}` },
            ...(hasTributosData ? [{ field: 'deducoes', label: `Deduções` }] : []),
            { field: 'excluir', label: 'Excluir' },
          ]}
        />
      )}
    </div>
  );
}

export default ComponentesBaseDeCalculoFormItemNCM;
